import { useCallback, useEffect, useMemo, useState } from 'react'
import { navigate, PageProps } from 'gatsby'
import { useBoolean, useDebounce, useDynamicList } from 'ahooks'
import moment from 'moment'
import clsx from 'clsx'
import {
  ProductStatus,
  ProductVariantsUpdateInput,
  useContactRequiredDateListQuery,
  useProductImagesAppendMutation,
  useProductImagesDeleteMutation,
  useProductMapImageDeleteMutation,
  useProductMapImageUpdateMutation,
} from 'lib/graphql/storeadmin'
import {
  productsErrorHandling,
  ProductUpdateMutationVariables,
  productVariantsFormat,
  useProduct,
} from 'lib/hooks'
import Layout from 'components/layouts/tow-column'
import {
  Button,
  Card,
  Checkbox,
  Error as ErrorComponent,
  ErrorAlert,
  Form,
  ImageUpload,
  Info,
  message,
  SEO,
  showMessage,
  Space,
  Title,
  UploadChangeParam,
  UploadFile as BaseUploadFile,
} from 'components/common'
import {
  AdminOnlyReservation,
  ProductApprovalRequired,
  ProductOptions,
  ProductPrice,
  ProductSalePeriod,
  ProductStatusSelect,
  ProductTitleAndDescription,
  ProductVariants,
  SalesStatus,
  Variant,
} from 'components/products'
import { ProductFormValues } from 'pages/admin/products/new'
import { productsDefaultOptions } from 'lib/constants'
import { ellipsisText } from 'utils/custom-functions'
import ContactRequiredSettings from 'components/products/inputs/ContactRequiredSettings'
import ChargeUnit from 'components/products/inputs/ChargeUnit'
import BookingTimeSlotGroup from 'components/products/inputs/BookingTimeSlotGroup'

interface UploadFile extends BaseUploadFile {
  // アップロードされたファイルの ID
  objectId?: string
}

type Product = ReturnType<typeof useProduct>['product']

type ImageUploadProps = {
  id: string
  images: Product['images']
  action: string
}

type MapImageUploadProps = {
  id: string
  image: Product['mapImage']
  action: string
}

/**
 * 施設画像アップロードコンポーネント
 */
const ImagesUpload = ({
  id,
  images,
  action,
}: ImageUploadProps): JSX.Element => {
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [appendImages] = useProductImagesAppendMutation()
  const [deleteImages] = useProductImagesDeleteMutation()

  useEffect(() => {
    if (!Array.isArray(images)) return

    const fileList = images.map<UploadFile>((image) => ({
      uid: image.id,
      objectId: image.id,
      name: image.filename,
      url: image.src,
      status: 'done',
    }))
    setFileList(fileList)
  }, [images])

  /**
   * ファイルアップロード時の処理
   * @param file
   * @param fileList
   * @param event
   */
  const handleImageChange = async ({
    file,
    fileList,
    event,
  }: UploadChangeParam) => {
    if (file.status !== 'done') {
      return setFileList(fileList)
    }

    const handleError = (file: UploadFile) => {
      setFileList((state) =>
        state.map<UploadFile>((_file) =>
          // アップロードに失敗したファイルはステータスを error に変更する
          file.uid === _file.uid ? { ..._file, status: 'error' } : _file
        )
      )
    }

    try {
      const {
        response: { key, encoding },
        size,
        name: filename,
        type: mimeType,
      } = file
      const { data } = await appendImages({
        variables: {
          id,
          images: [{ key, filename, mimeType, size, encoding }],
        },
      })
      const userErrors = data.productImagesAppend.userErrors
      if (userErrors.length > 0) {
        message.error('画像のアップロード時にエラーが発生しました。')
        handleError(file)
      } else {
        setFileList(
          fileList.map<UploadFile>((_file) =>
            // objectId を追加する
            // onRemove 時にアップロードに成功したファイルなのかどうかを判別できるようにするため
            file.uid === _file.uid
              ? { ..._file, objectId: data.productImagesAppend.newImages[0].id }
              : _file
          )
        )
      }
    } catch (e) {
      message.error('画像のアップロード時にエラーが発生しました。')
      handleError(file)
    }
  }

  /**
   * ファイル削除時の処理
   * @param file
   */
  const handleImageRemove = async (file: UploadFile): Promise<boolean> => {
    const handleError = () => {
      message.error('ファイルの削除時にエラーが発生しました。')
    }
    const target = fileList.filter((_file) => file.uid === _file.uid)[0]

    if (typeof target.objectId !== 'string') {
      // DB に登録されていないファイルなので何もせず処理終了
      return true
    }

    try {
      const { data } = await deleteImages({
        variables: {
          id,
          imageIds: target.objectId,
        },
      })
      const userErrors = data.productImagesDelete.userErrors
      if (userErrors.length > 0) {
        handleError()
        return false
      }
    } catch (e) {
      handleError()
      return false
    }

    return true
  }

  return (
    <>
      <ImageUpload
        name="images"
        fileList={fileList}
        action={action}
        method="POST"
        accept={['.jpeg', '.jpg', '.png', '.gif', '.webp']}
        max={5}
        onChange={handleImageChange}
        onRemove={handleImageRemove}
      />
    </>
  )
}

/**
 * 地図画像アップロードコンポーネント
 */
const MapImageUpload = ({
  id,
  image,
  action,
}: MapImageUploadProps): JSX.Element => {
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [updateMapImage] = useProductMapImageUpdateMutation()
  const [deleteMapImage] = useProductMapImageDeleteMutation()

  useEffect(() => {
    if (typeof image === 'undefined' || image === null) return

    setFileList([
      {
        uid: image.id,
        objectId: image.id,
        name: image.filename,
        url: image.src,
        status: 'done',
      },
    ])
  }, [image])

  /**
   * ファイルアップロード時の処理
   * @param file
   * @param fileList
   * @param event
   */
  const handleImageChange = async ({
    file,
    fileList,
    event,
  }: UploadChangeParam) => {
    if (file.status !== 'done') {
      return setFileList(fileList)
    }

    const handleError = (file: UploadFile) => {
      setFileList((state) =>
        state.map<UploadFile>((_file) =>
          // アップロードに失敗したファイルはステータスを error に変更する
          file.uid === _file.uid ? { ..._file, status: 'error' } : _file
        )
      )
    }

    try {
      const {
        response: { key, encoding },
        size,
        name: filename,
        type: mimeType,
      } = file
      const { data } = await updateMapImage({
        variables: {
          id,
          image: { key, encoding, size, filename, mimeType },
        },
      })
      const userErrors = data.productMapImageUpdate.userErrors
      if (userErrors.length > 0) {
        message.error('画像のアップロード時にエラーが発生しました。')
        handleError(file)
      } else {
        setFileList(fileList)
      }
    } catch (e) {
      message.error('画像のアップロード時にエラーが発生しました。')
      handleError(file)
    }
  }

  /**
   * ファイル削除時の処理
   * @param file
   */
  const handleImageRemove = async (file: UploadFile): Promise<boolean> => {
    const handleError = () => {
      message.error('ファイルの削除時にエラーが発生しました。')
    }

    try {
      const { data } = await deleteMapImage({
        variables: { id },
      })
      const userErrors = data.productMapImageDelete.userErrors
      if (userErrors.length > 0) {
        handleError()
        return false
      }
    } catch (e) {
      handleError()
      return false
    }

    return true
  }

  return (
    <>
      <ImageUpload
        name="images"
        fileList={fileList}
        action={action}
        method="POST"
        accept={['.jpeg', '.jpg', '.png', '.gif', '.webp']}
        max={1}
        onChange={handleImageChange}
        onRemove={handleImageRemove}
      />
    </>
  )
}

const onBack = () => navigate('../')

export const Head = () => <SEO title="施設詳細" />

export type BookingTimeSlotGroupList = {
  id: string
  name: string
  percentageOfProduct: number
}[]

/**
 * ページコンポーネント
 */
const IdPage = ({ params: { id } }: PageProps<Queries.Query>): JSX.Element => {
  const { product, fileUploadUrl, error, loading, update, refetch } =
    useProduct(id, {})
  const [form] = Form.useForm<ProductFormValues>()
  const [checkboxVariants, checkboxVariantsAction] = useState(false)
  const [showPeriodOfSales, setPeriodOfSales] = useState(false)
  const [showCheckbox, setShowCheckbox] = useState(true)
  const [optionsHaveValue, setOptionsHaveValue] = useBoolean(false)
  const [storeVariants, setStoreVariants] = useState(null)
  const [formValues, setFormValues] = useState<ProductFormValues>(null)
  const [requesting, setRequesting] = useBoolean(false)
  const [haveDuplicateOptionValue, setHaveDuplicateOptionsValue] =
    useState(false)
  const [contactRequiredDates, setContactRequiredDates] = useState<string[]>([])
  const [bookingTimeSlotGroupList, setBookingTimeSlotGroupList] =
    useState<BookingTimeSlotGroupList[]>()
  const [showProductsErrorAlert, showProductsErrorAlertAction] =
    useBoolean(false)
  const [productsErrorDescriptions, setProductsErrorDescriptions] = useState<
    string[]
  >([])
  const {
    data: alreadySelectedDates,
    loading: alreadySelectedDatesLoading,
    refetch: alreadyContactRequiredDatesRefetch,
  } = useContactRequiredDateListQuery({
    variables: { id },
  })

  const debounceFormValues = useDebounce(formValues, { wait: 1500 })

  const isSelectedOptions = useMemo(() => {
    return (
      typeof product?.variants[0]?.selectedOptions[0]?.name?.jaJp !==
      'undefined'
    )
  }, [product])

  const isPeriodOfSales = useMemo(() => {
    return (
      typeof product?.variants[0]?.periodForSale !== 'undefined' &&
      product?.variants[0]?.periodForSale !== null
    )
  }, [product])

  const productsVariants = useMemo(() => {
    if (product?.variants?.length >= 1 && !showCheckbox) {
      return product?.variants?.map((variant) => ({
        key: variant?.id,
        name: variant?.title?.jaJp,
        selectedOptions: variant?.selectedOptions
          .map(({ value }) => value?.jaJp)
          .toString()
          .replace(/,/g, ' / '),
        price: variant?.price,
        specialPrice: variant?.specialPrice,
        periodForSale: variant?.periodForSale,
        position: variant?.position,
        inventory: variant?.inventory,
        availableForSale: variant?.availableForSale,
        unit: variant?.unit,
        percentageOfProduct: variant?.percentageOfProduct,
        relatedVariations: variant?.relatedVariations,
        productId: product?.id,
        productName: product?.name?.jaJp,
      }))
    } else {
      // バリエーションが未設定の施設はvariantsにバリエーションが1つのみ格納されているため、
      // リストの先頭を施設の初期値としてセットする
      return [
        {
          key: product?.variants[0]?.id,
          price: product?.variants[0]?.price,
          specialPrice: product?.variants[0]?.specialPrice,
          periodForSale: product?.variants[0]?.periodForSale,
          inventory: product?.variants[0]?.inventory,
          availableForSale: product?.variants[0]?.availableForSale,
          unit: product?.variants[0]?.unit,
        },
      ]
    }
  }, [product, showCheckbox])

  const productsOptionsName = useMemo(() => {
    return product?.options?.map(({ name }) => name.jaJp)
  }, [product])

  const productsOptionsValue = useMemo(() => {
    return product?.options?.map(({ values }) => values)
  }, [product])

  const productVariationsMemo = useMemo(
    () => {
      const productsOptions = debounceFormValues?.productsOptions
      let editedProductVariants = []

      if (productsOptions?.length > 0) {
        const editedVariants = Array.from(
          new Set(debounceFormValues?.params)
        ).filter((e) => typeof e !== 'undefined')
        editedProductVariants = editedVariants
      }

      const { variants, activeOptionValue, storeVariants } =
        productVariantsFormat({
          productsOptions: productsOptions,
          productsVariants: productsVariants,
          productsOptionsValue: productsOptionsValue,
          editedProductVariants: editedProductVariants,
        })
      return { variants, activeOptionValue, storeVariants }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debounceFormValues]
  )

  // const [hasChangedList, setHasChangedList] = useState(false)
  // const prevListRef = useRef<Variant[] | null>(null)
  // バリエーションのリストを管理するカスタムフック
  const { list, remove, getKey, resetList } = useDynamicList<Variant>([
    ...(productVariationsMemo.variants.length > 0
      ? productVariationsMemo.variants
      : productsVariants),
  ])

  // useEffect(() => {
  //   const prevList = prevListRef.current
  //   if (prevListRef.current && JSON.stringify(prevList) !== JSON.stringify(list)) {
  //     setHasChangedList(true)
  //   } else {
  //     setHasChangedList(false)
  //   }
  //   prevListRef.current = list
  // }, [list])

  useEffect(
    () => {
      if (isSelectedOptions) {
        checkboxVariantsAction(true)
        setShowCheckbox(false)
        setOptionsHaveValue.setTrue()
        setHaveDuplicateOptionsValue(true)
      } else if (checkboxVariants) {
        form.setFieldsValue({
          productsOptions: productsDefaultOptions,
          productsVariants: [],
        })
      }
      if (isPeriodOfSales) {
        setPeriodOfSales(true)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSelectedOptions, isPeriodOfSales]
  )

  useEffect(() => {
    // Formatting data as Shopify and set data onchange
    const { variants, activeOptionValue, storeVariants } = productVariationsMemo
    if (activeOptionValue[0]?.length > 0) {
      setOptionsHaveValue.setTrue()
    }
    setStoreVariants(storeVariants)
    if (variants?.length > 0) {
      form.setFieldsValue({
        productsVariants: variants && variants,
      })
    }
  }, [
    productVariationsMemo,
    form,
    setFormValues,
    setOptionsHaveValue,
    setStoreVariants,
  ])

  const handleArchive = async (status: ProductStatus) => {
    const variables: ProductUpdateMutationVariables = {
      id,
      input: {
        status,
      },
    }
    const { data: result } = await update({ variables })
    const { productUserErrors } = result.productUpdate
    if (productUserErrors.length > 0) {
      throw productUserErrors
    }
  }

  const archive = async (): Promise<void> => {
    try {
      await handleArchive(ProductStatus.Archived)
      showMessage('success', 'アーカイブしました。')
    } catch (e) {
      showMessage('error', '問題が発生しました。')
      console.error(e)
    }
  }

  const unarchive = async (): Promise<void> => {
    try {
      await handleArchive(ProductStatus.Active)
      showMessage('success', 'アクティブになりました。')
    } catch (e) {
      showMessage('error', '問題が発生しました。')
      console.error(e)
    }
  }

  /**
   * BookingTimeSlotGroupListから渡されたIDと一致するitemを返す
   */
  const getBookingTimeSlotGroupItemByVariantId = (variantId: string) => {
    return bookingTimeSlotGroupList?.find((item) => {
      return item.some((variant) => variant.id === variantId)
    })
  }

  /**
   * BookingTimeSlotGroupItemから渡されたvariantIdと一致する要素を持つvariantsのidの配列を返す
   */
  const getRelatedVariantsIdsByVariantId = (variantId: string) => {
    const item = getBookingTimeSlotGroupItemByVariantId(variantId)
    if (!item) return []
    return item.map((variant) => variant.id) ?? []
  }

  /**
   * BookingTimeSlotGroupItemから渡されたIDと一致するvariantのpercentageOfProductを返す
   */
  const getPercentageOfProductByVariantId = (variantId: string) => {
    const item = getBookingTimeSlotGroupItemByVariantId(variantId)
    if (!item) return null
    return (
      item?.find((variant) => variant.id === variantId)?.percentageOfProduct ??
      0
    )
  }

  /**
   * Submit Button が押された際の処理
   * @param values
   */
  const onFinish = async (values: ProductFormValues): Promise<void> => {
    try {
      setRequesting.setTrue()
      const {
        title,
        description,
        status,
        approvalRequired,
        adminOnlyReservation,
        salesStatus,
        price,
        specialPrice,
        productsOptions,
        periodForSale,
        baseTimeUnit,
        unit,
      } = values
      if (
        typeof periodForSale !== 'undefined' &&
        periodForSale !== null &&
        periodForSale?.[1] <= periodForSale?.[0]
      ) {
        throw new Error('終了日は開始日よりも大きくする必要があります。')
      }
      if (checkboxVariants && productsOptions?.length <= 0) {
        throw new Error('オプションを追加してください')
      }
      if (
        !checkboxVariants &&
        showPeriodOfSales &&
        typeof periodForSale === 'undefined'
      ) {
        throw new Error('販売期間を確認してください。')
      }

      let productVariants: Variant[] = []
      if (productsOptions?.length > 0) {
        const variants = Array.from(new Set(values?.params)).filter(
          (element) => element !== undefined
        )
        productVariants = variants
      }
      if (productVariants?.length > 0) {
        if (productVariants.length > 100) {
          throw new Error('バリエーションは最大100個までです。')
        }
      }

      const productOptionsNames: string[] =
        productsOptions?.length > 0 &&
        productsOptions?.map((option) => option?.options)

      const newlyDateList: string[] = []
      const deleteDateList: string[] = []

      alreadySelectedDates?.contactRequiredDateList.forEach((date) => {
        if (!contactRequiredDates.includes(date)) {
          deleteDateList.push(date)
        }
      })
      contactRequiredDates.forEach((date) => {
        if (!alreadySelectedDates?.contactRequiredDateList.includes(date)) {
          newlyDateList.push(date)
        }
      })

      const variables: ProductUpdateMutationVariables = {
        id,
        input: {
          name: title,
          description: description,
          status: status,
          baseTimeUnit: baseTimeUnit,
          approvalRequired: approvalRequired,
          adminOnlyReservation: adminOnlyReservation,
          options: productOptionsNames?.length > 0 ? productOptionsNames : [],
          variants:
            productVariants?.length > 0
              ? productVariants?.map<ProductVariantsUpdateInput>(
                  (variant, index) => ({
                    price: Number(variant.price),
                    specialPrice: Number(variant.specialPrice),
                    inventory: 0,
                    availableForSale: Boolean(variant.availableForSale),
                    selectedOptions:
                      variant.selectedOptions?.length > 0 &&
                      variant.selectedOptions?.toString().split(' / '),
                    periodForSale:
                      typeof variant?.periodForSale !== 'undefined' &&
                      variant?.periodForSale !== null
                        ? {
                            start: moment(
                              variant?.periodForSale?.start
                            )?.format('YYYY-MM-DD'),
                            end: moment(variant?.periodForSale?.end)?.format(
                              'YYYY-MM-DD'
                            ),
                          }
                        : null,
                    unit: variant.unit,
                    percentageOfProduct: getPercentageOfProductByVariantId(
                      String(productsVariants[index]?.key ?? '')
                    ),
                    relatedVariations: getRelatedVariantsIdsByVariantId(
                      String(productsVariants[index]?.key ?? '')
                    ),
                  })
                )
              : [],
          price: Number(price) || null,
          periodForSale:
            typeof periodForSale !== 'undefined' &&
            typeof periodForSale?.[0] !== 'undefined'
              ? {
                  start: periodForSale?.[0]?.format('YYYY-MM-DD'),
                  end: periodForSale?.[1]?.format('YYYY-MM-DD'),
                }
              : null,
          specialPrice: Number(specialPrice) || null,
          inventory: 0,
          availableForSale: Boolean(salesStatus),
          contactRequiredInput: {
            addedList: newlyDateList,
            deletedList: deleteDateList,
          },
          unit: unit,
        },
      }

      const { data: result } = await update({ variables })
      const { productUserErrors } = result.productUpdate
      if (productUserErrors?.length > 0) {
        showProductsErrorAlertAction.setTrue()
        showMessage('error', 'エラーが発生しました。')
        const productsErrorDescriptions =
          productsErrorHandling(productUserErrors)
        setProductsErrorDescriptions(productsErrorDescriptions)
      } else if (error) {
        throw error
      } else {
        showProductsErrorAlertAction.setFalse()
        showMessage('success', '保存しました。')
      }
    } catch (e) {
      console.error(e)
      showProductsErrorAlertAction.setTrue()
      setProductsErrorDescriptions([e.toString()])
      showMessage(
        'error',
        <>
          <div>施設更新時にエラーが発生しました</div>
          <div>{e.message}</div>
        </>
      )
    } finally {
      setRequesting.setFalse()
      await refetch({ id })
      await alreadyContactRequiredDatesRefetch()
    }
  }

  const variantDelete = (list: Variant[], index: number) => {
    const productsOptions = form.getFieldValue('productsOptions')
    const productsVariants = form.getFieldValue('productsVariants')
    const newList = list.filter((_, i) => i !== index)

    let uniqueStoredVariants = []
    if (storeVariants?.length === 0) {
      const { storeVariants: getStoreVariants } = productVariantsFormat({
        productsOptions: productsOptions,
        productsVariants: productsVariants,
      })
      uniqueStoredVariants = Array.from(new Set(getStoreVariants))
    } else {
      uniqueStoredVariants = Array.from(new Set(storeVariants))
    }

    if (newList) {
      const selectedOptions = newList?.map((value, index) =>
        value?.selectedOptions?.toString().split(' / ')
      )

      // Get selected options as an array
      const selectedOptionsArray = []
      selectedOptions?.map((array) =>
        array?.forEach((value) => selectedOptionsArray.push(value))
      )

      const uniqueSelectedOptions = Array.from(new Set(selectedOptionsArray))

      let removedField
      if (uniqueSelectedOptions.length > 0) {
        uniqueStoredVariants.forEach((v) => {
          // checked Removed item & get the item
          if (!uniqueSelectedOptions?.includes(v)) {
            removedField = v
          }
        })
      }

      // Stored ProductOptionsValues after delete item
      const removedProductOptionsValues = []

      if (removedField?.length > 0) {
        productsOptions.map((v) =>
          removedProductOptionsValues.push({
            // deleted match item from productOptionsValues
            options: v?.options,
            productOptionsValues: v?.productOptionsValues?.filter(
              (x) => x.optionsValue !== removedField
            ),
          })
        )
      }

      const uniqueRemovedProductOptionsValues = Array.from(
        new Set(removedProductOptionsValues)
      )
      if (
        uniqueRemovedProductOptionsValues?.length > 0 &&
        uniqueRemovedProductOptionsValues[0]?.length !== 0
      ) {
        form.setFieldsValue({
          productsOptions: uniqueRemovedProductOptionsValues,
        })
        setStoreVariants(uniqueSelectedOptions)
      }
    }
    if (list?.length === 1) {
      checkboxVariantsAction(false)
      setShowCheckbox(true)
      setOptionsHaveValue.setFalse()
      setFormValues(null)
    }
  }
  const onOptionDelete = () => {
    const productOptions = form.getFieldValue('productsOptions')
    if (productOptions?.length === 1) {
      checkboxVariantsAction(false)
      setShowCheckbox(true)
      setOptionsHaveValue.setFalse()
      setFormValues(null)
    }
  }
  const onCheckboxToggle = () => {
    onOptionDelete()
    form.setFieldsValue({
      productsOptions: productsDefaultOptions,
      productsVariants: undefined,
      periodForSale: undefined,
    })
    setStoreVariants([])
    setPeriodOfSales(!showPeriodOfSales)
    checkboxVariantsAction(!checkboxVariants)
  }

  const checkProductOptionExist = useCallback(
    (currentValue) => {
      const productsOptions = form.getFieldValue('productsOptions')
      const productOptionsValues = []
      productsOptions?.forEach((value) => {
        value?.productOptionsValues?.forEach((options) =>
          productOptionsValues.push(options?.optionsValue)
        )
      })
      return productOptionsValues?.filter((data) => data === currentValue)
    },
    [form]
  )

  const onChangeFormValues = useCallback(
    (currentUpdate, values) => {
      const currentOption = currentUpdate?.productsOptions

      let currentChangedOptionsValue
      if (
        typeof currentOption !== 'undefined' &&
        currentOption[currentOption?.length - 1]?.productOptionsValues
      ) {
        currentChangedOptionsValue =
          currentOption[currentOption?.length - 1]?.productOptionsValues[
            currentOption[currentOption?.length - 1]?.productOptionsValues
              .length - 1
          ]?.optionsValue
      }

      if (typeof currentChangedOptionsValue !== 'undefined') {
        const matchValue = checkProductOptionExist(currentChangedOptionsValue)
        if (matchValue?.length < 2) {
          setFormValues(values)
        }
      }
    },
    [checkProductOptionExist]
  )

  /**
   * バリエーションIDからバリエーションの詳細情報を取得する
   */
  const getVariantByVariantId = useCallback(
    (variantId: string) => {
      return product.variants.find((variant) => variant.id === variantId)
    },
    [product?.variants]
  )

  useEffect(() => {
    // バリエーションが1つのみの場合は関連バリエーションの組み合わせを取得しない
    if (!product || product?.variants?.length <= 1) return
    // 重複していない関連バリエーションの組み合わせを取得
    const uniqueRelatedVariationList: string[][] = []
    product.variants.forEach((variant) => {
      if (!variant.relatedVariations || variant.relatedVariations.length < 1)
        return
      // 重複していないかチェック
      const isUnique = uniqueRelatedVariationList.every(
        (item) =>
          item.length !== variant.relatedVariations.length ||
          item.some(
            (value, index) => value !== variant.relatedVariations[index]
          )
      )
      if (isUnique) {
        uniqueRelatedVariationList.push(variant.relatedVariations)
      }
    })

    // 関連バリエーションの組み合わせからグループ化したリストを作成
    const newRelatedVariationGroupList: BookingTimeSlotGroupList[] =
      uniqueRelatedVariationList.map((item) =>
        item
          .map((variantid) => {
            // バリエーションIDからバリエーションの詳細情報を取得
            const variant = getVariantByVariantId(variantid)
            if (!variant || !variant.title) return null

            return {
              id: variant.id,
              name: variant.title.jaJp,
              percentageOfProduct: variant.percentageOfProduct,
            }
          })
          .filter(Boolean)
      )
    setBookingTimeSlotGroupList(newRelatedVariationGroupList)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.variants, getVariantByVariantId])

  if (error) {
    return ErrorComponent.render(error)
  }

  return (
    <>
      <Form
        form={form}
        component="form"
        onFinish={onFinish}
        name="editProducts"
        onValuesChange={onChangeFormValues}
      >
        <Layout
          title={ellipsisText(product?.name.jaJp, 12)?.text}
          loading={loading}
          onBack={onBack}
        >
          <Layout.Section size="large">
            <Layout.Item>
              {showProductsErrorAlert && (
                <ErrorAlert
                  onCloseAlert={showProductsErrorAlertAction.setFalse}
                  alertDescription={productsErrorDescriptions}
                />
              )}
              <ProductTitleAndDescription
                title={product?.name.jaJp}
                description={product?.description?.jaJp}
                loading={loading}
              />
            </Layout.Item>

            <Layout.Item>
              <Card shadow loading={loading}>
                <div className={clsx('flex', 'justify-content', 'mb-4')}>
                  <Title level={5} space="none">
                    施設画像
                  </Title>
                  <Info
                    title={
                      '最大で５つまで登録可能です。JPEG / PNG / GIF / WEBP ファイルのみアップロード可能です。画像のアスペクト比は高さ 720px 幅 1000px です。'
                    }
                  />
                </div>

                <ImagesUpload
                  id={id}
                  action={fileUploadUrl}
                  images={product?.images || []}
                />
              </Card>
            </Layout.Item>

            <Layout.Item>
              <Card shadow loading={loading}>
                <div className={clsx('flex', 'justify-content', 'mb-4')}>
                  <Title level={5} space="none">
                    地図画像
                  </Title>
                  <Info
                    title={
                      '最大で１つまで登録可能です。JPEG / PNG / GIF / WEBP ファイルのみアップロード可能です。画像のアスペクト比は高さ 720px 幅 1000px です。'
                    }
                  />
                </div>

                <MapImageUpload
                  id={id}
                  action={fileUploadUrl}
                  image={product?.mapImage}
                />
              </Card>
            </Layout.Item>

            {/* 管理者の承認が必要か？ */}
            <Layout.Item>
              <ProductApprovalRequired
                isApprovalRequired={product?.approvalRequired}
                loading={loading}
              />
            </Layout.Item>

            {/* 管理者のみ予約可能か？ */}
            <Layout.Item>
              <AdminOnlyReservation
                isAdminOnlyReservation={product?.adminOnlyReservation}
                loading={loading}
              />
            </Layout.Item>

            {!checkboxVariants && (
              <>
                {/* 料金設定 */}
                <Layout.Item>
                  <ProductPrice
                    price={productsVariants[0]?.price}
                    specialPrice={productsVariants[0]?.specialPrice}
                    unit={productsVariants[0]?.unit}
                    loading={loading}
                  />
                </Layout.Item>

                {/* 販売期間 */}
                <Layout.Item>
                  <Card shadow loading={loading}>
                    <Info
                      title={
                        '販売期間を設定すると、その期間のみ予約可能になります。'
                      }
                    >
                      <Title level={5} space="none">
                        販売期間
                      </Title>
                    </Info>
                    <div className="mt-4">
                      <Checkbox
                        checked={showPeriodOfSales}
                        onChange={() => {
                          form.setFieldsValue({
                            periodForSale: undefined,
                          })
                          setPeriodOfSales((val) => !val)
                        }}
                      >
                        この施設には販売期間があります。
                      </Checkbox>
                      {showPeriodOfSales && (
                        <ProductSalePeriod
                          periodForSale={
                            productsVariants[0]?.periodForSale && [
                              moment(productsVariants[0]?.periodForSale?.start),
                              moment(productsVariants[0]?.periodForSale?.end),
                            ]
                          }
                          loading={loading}
                        />
                      )}
                    </div>
                  </Card>
                </Layout.Item>

                {/* 販売状況 */}
                <Layout.Item>
                  <SalesStatus
                    loading={loading}
                    availableForSale={productsVariants[0].availableForSale}
                  />
                </Layout.Item>
              </>
            )}
            <Layout.Item>
              <ContactRequiredSettings
                loading={alreadySelectedDatesLoading}
                alreadyContactRequiredDates={
                  alreadySelectedDates?.contactRequiredDateList
                }
                setContactRequiredDates={setContactRequiredDates}
              />
            </Layout.Item>
          </Layout.Section>

          <Layout.Section size="small">
            <Layout.Item>
              <ProductStatusSelect
                status={product?.status}
                loading={loading}
                onUnarchive={unarchive}
              />
            </Layout.Item>

            <Layout.Item>
              <ChargeUnit
                baseTimeUnit={product?.baseTimeUnit}
                loading={loading}
              />
            </Layout.Item>
          </Layout.Section>
        </Layout>

        <Layout title="">
          <Layout.Section size="large">
            <Card shadow loading={loading}>
              <Title level={5}>オプション</Title>

              {showCheckbox && (
                <Checkbox
                  checked={checkboxVariants}
                  onChange={onCheckboxToggle}
                >
                  この施設には複数のオプションがあります。
                </Checkbox>
              )}

              {checkboxVariants && (
                <ProductOptions
                  onLastOptionDelete={onOptionDelete}
                  productsOptionsName={productsOptionsName}
                  productsOptionsValue={productsOptionsValue}
                  haveDuplicateOptionValue={haveDuplicateOptionValue}
                  setHaveDuplicateOptionsValue={setHaveDuplicateOptionsValue}
                />
              )}
            </Card>
          </Layout.Section>

          {checkboxVariants && optionsHaveValue && (
            <Layout.Section size="large">
              <Card className={clsx('mt-10')}>
                <ProductVariants
                  form={form}
                  onVariantDelete={variantDelete}
                  productsVariantsData={
                    productVariationsMemo.variants.length > 0
                      ? productVariationsMemo.variants
                      : productsVariants || []
                  }
                  list={list}
                  remove={remove}
                  getKey={getKey}
                  resetList={resetList}
                  dependence={debounceFormValues}
                />
              </Card>
            </Layout.Section>
          )}

          {/* 予約枠のグループ化 */}
          {productsVariants.length > 1 && (
            <Layout.Section size="large">
              <Card shadow loading={loading}>
                {/* {hasChangedList &&
                  <div className="text-red-500 mb-8">
                    グループの追加や更新をする場合は、先に
                    <Button
                      type="link"
                      htmlType="submit"
                      loading={requesting}
                      disabled={loading}
                      style={{ padding: "0 8px" }}
                    >
                      保存する
                    </Button>
                    をクリックしてください
                  </div>
                } */}
                <div className="flex items-center space-x-1 mb-4">
                  <Title level={5} space="none">
                    予約枠共有グループの設定
                  </Title>
                  <Info
                    title={
                      <div>
                        予約枠を共有するバリエーションのグループを設定できます。
                        <br />
                        グループ化されたバリエーションは1つの予約枠を共有し、
                        各バリエーションに設定された予約枠占有率に基づいて予約可能かどうかが決定されます。
                      </div>
                    }
                  />
                </div>
                <BookingTimeSlotGroup
                  // hasChangedList={hasChangedList}
                  productVariants={product?.variants}
                  bookingTimeSlotGroupList={bookingTimeSlotGroupList}
                  setBookingTimeSlotGroupList={setBookingTimeSlotGroupList}
                />
              </Card>
            </Layout.Section>
          )}

          <Layout.Footer
            left={
              <Space>
                {product?.status === ProductStatus.Archived ? (
                  <Space>
                    <Button onClick={unarchive}>アーカイブを解除する</Button>
                  </Space>
                ) : (
                  <Space>
                    <Button onClick={archive}>アーカイブする</Button>
                  </Space>
                )}
              </Space>
            }
            right={
              <div className={clsx('flex', 'justify-end')}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={requesting}
                  disabled={loading}
                >
                  保存する
                </Button>
              </div>
            }
          />
        </Layout>
      </Form>
    </>
  )
}

export default IdPage
